
import {Bank, Contact, Purchase, Sale} from "@/models/base";
import Vue from "vue";
import AppSelectBankKind from "./SelectBankKind.vue";
import AppSelectBankType from "./SelectBankType.vue";
import AppContactSearch from "@/components/contacts/ContactSearchWithBrowseAndCreate.vue";
import AppPurchaseSearch from "@/components/purchases/PurchaseSearchWithBrowse.vue";
import AppSaleSearch from "@/components/sales/SaleSearchWithBrowseForBankEdit.vue";
import {SaleService} from "@/services/sale-service";
import {BankKind} from "@/models/base/bank";

import {PurchaseService} from "@/services/purchase-service";
import {CustomerService} from "@/services/customer-service";
import {SupplierService} from "@/services/supplier-service";
import {HttpError} from "@/utils/services/http-error";
import AppLedgerAccountAutocomplete from "@/components/common/LedgerAccountAutocomplete.vue";
import BankBalance from "@/models/base/bank-balance";
import {BankService} from "@/services/bank-service";
import AppCustomTable from "@/components/common/CustomTable.vue";
import {printHumanFloat} from "@/utils/pretty-print";
import {ContactType} from "@/models/base/contact";
import {DossierConfigService} from "@/services";
import { BookYearService } from "@/services/book-year-service";
import { IBankBookingFavorite } from "@/models/dossier-configs/bank-config";
import { isSaleTypeAllowedForBankBookings } from "@/utils/bank-booking-sale-types";
import { ISelectItem } from "winfakt-vue-components";
import { isGreaterDate, isSmallerDate } from "@/utils/date-utils";


export default Vue.extend({
	data(){
		return{
			showDc: false,
			debet: true,
			originalTotal: 0,
			originalBank: new Bank(),
			disableTotal: false,
			balances: [] as BankBalance[],
			batch: [] as  (Sale | Purchase)[],
			config: DossierConfigService.getBookkeepingConfig(),
			bankConfig: DossierConfigService.getBankConfig(),
			selectedFavoriteId: 0,
			showFavoriteNameModal: false,
			favoriteName: "",
			savingFavorite: false
		};
	},
	props: {
		bank: {
			type: Object as ()=>Bank,
			required: true
		},
		clearBatch: {
			type: Number,
			required: false,
		},
		multiple: {
			type: Boolean,
			default: true
		},
		history: Array as () => Bank[],
		lockBankMovement: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		currentBalance():number{
			return this.balances.filter(c => c.TypeID == this.bank.TypeID)[0]?.Total || 0;
		},
		batchTotalBooked():number{

			if (!this.history || this.history.length === 0) {
				return 0;
			}
			return this.history.reduce((total:number, item:any) => {
				return total + (item.TotalDC || 0);
			}, 0);

		},
		newBalance():string{
			if(this.debet){
				return printHumanFloat((this.currentBalance-this.originalTotal) + (this.batchTotalBooked + this.bank.Total));
			}
			return printHumanFloat((this.currentBalance-this.originalTotal) - (this.batchTotalBooked + this.bank.Total));
		},
		locale():string{
			return this.$i18n.locale;
		},
		selectableContactGroups():number[]{
			if (this.bank.Kind == BankKind.Diverse){
				return [1,2];
			}
			if (this.bank.Kind == BankKind.Sale){
				return [1];
			}
			if (this.bank.Kind == BankKind.Purchase) {
				return [2];
			}
			return [];
		},
		disableLedgerInput(): boolean{
			return this.bank.Kind != BankKind.Diverse;
		},
		bankDifference(): string{
			if(!this.originalBank)
				return "0";
			return printHumanFloat(this.originalBank.TotalDC-this.bank.TotalDC);
		},
		bookYearStartDate():Date {
			return BookYearService.getBookYearStartDate(this.bank.BookYear || BookYearService.selectedBookYear);
		},
		bookYearEndDate():Date {
			return BookYearService.getBookYearEndDate(this.bank.BookYear || BookYearService.selectedBookYear);
		},
		favorites():IBankBookingFavorite[]{
			return this.bankConfig.Favorites;
		},
		favoriteItems():ISelectItem<number>[]{
			return this.favorites.map(f=>{
				return {text: f.Name, value: f.ID} as ISelectItem<number>;
			});
		},
		selectedFavorite():IBankBookingFavorite | null{
			return this.favorites.find(f=>f.ID == this.selectedFavoriteId) || null;
		},
		isDateValid():boolean{
			if (isGreaterDate(this.bank.TimeCreated, this.bookYearEndDate)){
				return false;
			}
			if (isSmallerDate(this.bank.TimeCreated, this.bookYearStartDate)){
				return false;
			}
			return true;
		},
		isValid():boolean{
			return this.isDateValid;
		},
		isBankMovementLocked():boolean{
			return this.lockBankMovement || !!this.bank.ID;
		}

	},
	watch: {
		"bank.InvoiceID": {
			immediate: true,
			handler(){
				this.fetchSale();
			}
		},
		"bank.PurchaseID": {
			immediate: true,
			handler(){
				this.fetchPurchase();
			}
		},
		"bank.ContactID": {
			immediate: true,
			handler(){
				this.fetchContact();
			}
		},
		"bank.Total": {
			immediate: true,
			handler(){
				this.fetchContact();
			}
		},
		"bank.ID": {
			immediate: true,
			handler(){
				if(this.originalTotal==0 && this.bank.TotalDC!=0){
					this.originalTotal = this.bank.TotalDC;
				}
				if(this.bank.TotalDC<0 && this.bank.Total>0 || this.bank.Total<0 && this.bank.TotalDC>0){
					this.debet = false;
				}
				this.enforceSaleSign();
			}
		},
		"clearBatch"(){
			this.batch = [];
		},
		"isValid": {
			immediate: true,
			handler(){
				this.$emit("valid", this.isValid);
			}
		}
	},
	methods: {
		switchTotalDc(){
			this.bank.TotalDC = this.debet ? this.bank.Total : this.bank.Total * -1;
		},
		applyKindSign(kind:BankKind){
			if (kind == BankKind.Sale){
				this.debet = true;
			} else if (kind == BankKind.Purchase) {
				this.debet = false;
			}
			if (!this.isBankMovementLocked) {
				this.switchTotalDc();
				return;
			}
			if (kind == BankKind.Sale){
				this.bank.Total = this.bank.TotalDC;
			} else if (kind == BankKind.Purchase) {
				this.bank.Total = this.bank.TotalDC * -1;
			}
		},
		async fetchSale(){
			if (this.bank.InvoiceID == 0){
				this.bank.FriendlySaleID = "";
				this.bank.Invoice = null;
				return;
			}
			if (this.bank.Invoice && this.bank.Invoice.ID == this.bank.InvoiceID) {
				return;
			}
			this.selectSale(await SaleService.getSale(this.bank.InvoiceID));
		},
		async fetchPurchase(){
			if (this.bank.PurchaseID == 0) {
				this.bank.FriendlyPurchaseID = "";
				this.bank.Purchase = null;
				return;
			}
			if (this.bank.Purchase && this.bank.PurchaseID == this.bank.Purchase.ID){
				return;
			}
			this.selectPurchase(await PurchaseService.getPurchase(this.bank.PurchaseID));
		},
		async fetchContact(){
			if (this.bank.ContactID == 0){
				this.bank.Contact = null;
				return;
			}
			if (this.bank.Contact && this.bank.ContactID == this.bank.Contact.ID) {
				return;
			}
			let contact:null | Contact = null;
			try{
				contact = await CustomerService.getContact(this.bank.ContactID);
			}catch(err){
				let error = err as HttpError;
				error.dontShow = true;
				contact = await SupplierService.getContact(this.bank.ContactID);
			}
			this.bank.Contact = contact;
		},
		selectSales(sales:Sale[]){
			let allowed = sales.filter(s => isSaleTypeAllowedForBankBookings(s.TypeID));
			if (allowed.length != sales.length) {
				this.$wf.notify(this.$ct("booking-not-allowed-for-sale-type").toString(), "danger", 3000, "exclamation-triangle");
			}
			if (allowed.length == 0) {
				return;
			}
			if(allowed.length == 1) {
				this.selectSale(allowed[0]);
				return;
			}
			this.bank.ID = 0;
			this.bank.Purchase = null;
			this.bank.PurchaseID = 0;
			this.bank.FriendlyPurchaseID = "";

			this.batch = allowed;
			let total = 0;
			for (let sale of this.batch) {
				total += sale.TotalOpen;
			}
			this.bank.Total = total;
			this.switchTotalDc();
			this.disableTotal = true;
			this.$emit("update", this.batch);
		},
		selectPurchases(purchases:Purchase[]){
			if(purchases.length == 1) {
				this.selectPurchase(purchases[0]);
				return;
			}
			this.bank.ID = 0;
			this.bank.Invoice = null;
			this.bank.InvoiceID = 0;
			this.bank.FriendlySaleID = "";

			this.batch = purchases;
			let total = 0;
			for (let purchase of this.batch) {
				total += purchase.TotalOpen;
			}
			this.bank.Total = total;
			this.switchTotalDc();
			this.disableTotal=true;
			this.$emit("update", this.batch);
		},
		async selectSale(sale:Sale){
			if (!isSaleTypeAllowedForBankBookings(sale.TypeID)) {
				this.$wf.notify(this.$ct("booking-not-allowed-for-sale-type").toString(), "danger", 3000, "exclamation-triangle");
				return;
			}
			this.bank.Invoice = sale;
			this.bank.FriendlySaleID = sale.ComputedFriendlyID;
			this.bank.Contact = sale.Contact;
			this.bank.ContactID = sale.ContactID;
			this.bank.Purchase = null;
			this.bank.PurchaseID = 0;
			this.bank.FriendlyPurchaseID = "";
			if (this.bank.InvoiceID == sale.ID){
				return;
			}
			this.bank.InvoiceID = sale.ID;
			if(this.originalBank.Kind==BankKind.Diverse && Math.round(Math.abs(this.bank.Total) *100) != Math.round(Math.abs(sale.TotalOpen) * 100)){
				let confirm = await this.$wf.confirm(this.$ct("bank-total-not-the-same",
					{amount: printHumanFloat(this.bank.Total),new: printHumanFloat(sale.TotalOpen)}),
					this.$t("common.yes"), this.$t("common.no"), this.$ct("common.warning"), "warning");
				if (confirm){
					this.bank.Total = sale.TotalOpen;
				}
			}else{
				this.bank.Total = sale.TotalOpen;
			}
			this.switchTotalDc();
			this.disableTotal=false;
		},
		onBankKindInput(kind:BankKind){
			this.bank.Kind = kind;
			this.applyKindSign(kind);
			if (kind == BankKind.Sale){
				this.bank.Purchase = null;
				this.bank.PurchaseID = 0;
				this.bank.LedgerAccountCode = null;
				if (!this.bank.Contact){
					return;
				}
				if (this.bank.Contact.GroupID != 1){
					this.emptyContact();
				}
			} else if (kind == BankKind.Purchase) {
				this.bank.Invoice = null;
				this.bank.InvoiceID = 0;
				this.bank.LedgerAccountCode = null;
				if (!this.bank.Contact){
					return;
				}
				if (this.bank.Contact.GroupID != 3){
					this.emptyContact();
				}
			}else {
				this.bank.Purchase = null;
				this.bank.PurchaseID = 0;
				this.bank.Invoice = null;
				this.bank.InvoiceID = 0;
				this.bank.LedgerAccountCode = this.config.DefaultSuspenseAccount;
				this.emptyContact();
			}
		},
		emptyContact(){
			this.bank.Contact = null;
			this.bank.ContactID = 0;
			this.bank.Invoice = null;
			this.bank.InvoiceID = 0;
			this.bank.FriendlySaleID = "";
		},
		async selectPurchase(purchase:Purchase){
			this.bank.Purchase = purchase;
			this.bank.FriendlyPurchaseID = purchase.ComputedFriendlyID;
			this.bank.PurchaseID = purchase.ID;

			if(this.originalBank.Kind==BankKind.Diverse && Math.round(Math.abs(this.bank.Total) * 100) != Math.round(Math.abs(purchase.TotalOpen)*100)){
				let confirm = await this.$wf.confirm(this.$ct("bank-total-not-the-same",
					{amount: printHumanFloat(this.bank.Total),new: printHumanFloat(purchase.TotalOpen)}),
					this.$t("common.yes"), this.$t("common.no"), this.$ct("common.warning"), "warning");
				if (confirm){
					this.bank.Total = purchase.TotalOpen;
					this.bank.TotalDC = -purchase.TotalOpen;
				}
			}else{
				this.bank.Total = purchase.TotalOpen;
				this.bank.TotalDC = -purchase.TotalOpen;
			}
			this.bank.InvoiceID = 0;
			this.bank.Invoice = null;
			this.bank.FriendlySaleID = "";
			this.bank.Contact = purchase.Contact;
			this.bank.ContactID = purchase.ContactID;
			this.disableTotal = false;
		},
		selectContact(contact:Contact){
			this.bank.Contact = contact;
			this.bank.ContactID = contact.ID;
			if(this.bank.Kind != BankKind.Diverse){
				return;
			}
			if (this.isBankMovementLocked) {
				this.debet = this.bank.TotalDC >= 0;
				return;
			}
			this.debet = contact.GroupID != ContactType.Supplier;
			this.switchTotalDc();
		},
		enforceSaleSign(){
			if (this.bank.Kind != BankKind.Sale){
				return;
			}
			this.debet = true;
			this.switchTotalDc();
		},
		onDebetCreditToggle(){
			this.debet=!this.debet;
			this.switchTotalDc();
			if  (this.$refs.total){
				(this.$refs.total as any).focus();
			}
		},
		getBatchCompanyName(item:Sale | Purchase):string {
			if (item.Contact && item.Contact.MainAddress) {
				return item.Contact.MainAddress.CompanyName || "";
			}
			return "";
		},
		getBatchTotalOpen(item:Sale | Purchase):string {
			return printHumanFloat(item.TotalOpen);
		},
		onLedgerAccountCode(code:string){
			if (this.bank.Kind == BankKind.Diverse){
				for (let b of BankService.getTypes()) {
					if (b.LedgerNumber == code) {
						// Run in next frame to prevent enters from immediatly closing the modal
						requestAnimationFrame(()=>{
							let messageData = {code, bankNr: b.ID, bankName: b.getName()};
							let message = this.$ct("components.bookkeeping.ledger-account-code-is-not-allowed-cause-used-in-bank", messageData);
							this.$wf.dialog(message, this.$ct("common.Ok"), this.$ct("common.error"), "warning");
						});
						return;
					}
				}
			}
			this.bank.LedgerAccountCode = code;
		},
		applyFavorite(id:number){
			this.selectedFavoriteId = id;
			let favorite = this.selectedFavorite;
			if (!favorite) return;
			this.bank.TypeID = favorite.TypeID;
			this.onBankKindInput(favorite.Kind);
			if (favorite.Kind == BankKind.Diverse && favorite.LedgerAccountCode){
				this.onLedgerAccountCode(favorite.LedgerAccountCode);
			}
			this.bank.Info = favorite.Info;
		},
		openSaveFavoriteModal(){
			this.favoriteName = "";
			this.showFavoriteNameModal = true;
		},
		async confirmSaveFavorite(){
			let name = this.favoriteName.trim();
			if (!name || this.savingFavorite) return;
			this.savingFavorite = true;
			try{
				let config = DossierConfigService.getBankConfig();
				let id = config.Favorites.reduce((max, f)=>Math.max(max, f.ID), 0) + 1;
				config.Favorites.push({
					ID: id,
					Name: name,
					TypeID: this.bank.TypeID,
					Kind: this.bank.Kind,
					LedgerAccountCode: this.bank.Kind == BankKind.Diverse ? (this.bank.LedgerAccountCode || "") : "",
					Info: this.bank.Info
				});
				this.bankConfig = await DossierConfigService.setBankConfig(config);
				this.selectedFavoriteId = id;
				this.showFavoriteNameModal = false;
				this.$wf.notify(this.$ct("favorites.saved").toString(), "success", 3000, "check");
			}finally{
				this.savingFavorite = false;
			}
		},
		async deleteFavorite(favorite:IBankBookingFavorite){
			let confirmed = await this.$wf.confirm(this.$ct("favorites.delete-confirm", {name: favorite.Name}),
				this.$t("common.yes"), this.$t("common.no"), this.$ct("common.warning"), "warning");
			if (!confirmed) return;
			let config = DossierConfigService.getBankConfig();
			let i = config.Favorites.findIndex(f=>f.ID == favorite.ID);
			if (i != -1){
				config.Favorites.splice(i, 1);
				this.bankConfig = await DossierConfigService.setBankConfig(config);
			}
			if (this.selectedFavoriteId == favorite.ID){
				this.selectedFavoriteId = 0;
			}
		}
	},
	mounted(){
		if(this.bank.TotalDC<0 && this.bank.Total>0 || this.bank.Total<0 && this.bank.TotalDC>0){
			this.debet = false;
		}
		this.originalBank = new Bank(this.bank.getJSON());
		this.enforceSaleSign();
	},
	async created(){
		this.balances = await BankService.getAllBalances();
	},
	components: {
		AppSelectBankKind,
		AppSelectBankType,
		AppContactSearch,
		AppSaleSearch,
		AppPurchaseSearch,
		AppLedgerAccountAutocomplete,
		AppCustomTable
	},
	i18nEx: {
		componentPrefix: "components.bank"
	}
});
